/**
 * Change-thumbnail dialog (#932).
 *
 * Uploads a custom image as the asset's thumbnail (stored backend-side as
 * the fs_smart conversion with user_provided=TRUE — it survives original
 * replace/reprocess) and offers revert-to-automatic when a custom
 * thumbnail is in use. 202-async: the mutations resolve when /data
 * reflects the change; grid/detail thumbnails refresh via invalidation +
 * cache-busted URLs.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useAtomValue } from 'jotai/utils';
import BaseModal from 'src/components/BaseModal/BaseModal';
import Button from 'src/components/Button';
import FileUpload from 'src/components/FileUpload/FileUpload';
import { useAssetDetailsQuery } from 'src/api/queries/assetsQueries';
import {
  useAssetThumbnailMutation,
  useAssetThumbnailRevertMutation,
} from 'src/api/mutations/assetContentAccessMutations';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { useAlerts } from 'src/common/AlertManager';
import useModal from 'src/hooks/useModal';
import { getThumbnailUrl, addCacheBuster, thumbnailVersion } from 'src/utils/previews';
import { Asset } from 'src/types/TAssets';
import { settingsAtom } from 'src/shared/settings/index';
import { StoryboardViewerModal, storyboardSourceFor } from 'src/components/Storyboard';
import messages from './messages';
import styles from './ChangeThumbnailDialog.module.scss';

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

type ChangeThumbnailDialogProps = {
  assetId: string;
};

export const ChangeThumbnailDialog: React.FC<ChangeThumbnailDialogProps> = ({ assetId }) => {
  const { formatMessage } = useIntl();
  const accessToken = useAccessToken();
  const { showInfo, showAlert } = useAlerts();
  const { closeModal, showModal } = useModal();
  const storyboardMode: string | undefined = useAtomValue(settingsAtom)?.video?.thumbnails?.storyboard?.mode;

  const { data: asset } = useAssetDetailsQuery(assetId) as { data: Asset | undefined };
  const isCustom = !!asset?.addons_info?.ON_DEMAND_IMAGE?.user_provided;
  // Videos: a frame of the storyboard is the other way to pick a thumbnail.
  const storyboard = asset ? storyboardSourceFor(asset, { mode: storyboardMode, jwt: accessToken }) : null;

  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');

  useEffect(() => {
    if (!pendingFile) {
      setPreviewUrl('');
      return;
    }
    const url = URL.createObjectURL(pendingFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingFile]);

  const version = thumbnailVersion(asset);
  const currentThumbnailUrl = useMemo(
    () => addCacheBuster(getThumbnailUrl({ width: 300, height: 300, id: assetId, jwt: accessToken, version })),
    [assetId, accessToken, version],
  );

  const uploadMutation = useAssetThumbnailMutation(assetId, {
    onSuccess: () => {
      showInfo(formatMessage(messages.uploaded));
      closeModal();
    },
    onError: () => showAlert(formatMessage(messages.uploadFailed)),
  });

  const revertMutation = useAssetThumbnailRevertMutation(assetId, {
    onSuccess: () => {
      showInfo(formatMessage(messages.reverted));
      closeModal();
    },
    onError: () => showAlert(formatMessage(messages.revertFailed)),
  });

  const busy = uploadMutation.isLoading || revertMutation.isLoading;

  return (
    <BaseModal containerStyle={{ width: 'min(560px, 92vw)' }}>
      <div className={styles.dialog}>
        <h3>{formatMessage(messages.title)}</h3>
        <p>{formatMessage(messages.explainer)}</p>
        <p className={styles.cacheNote}>{formatMessage(messages.cacheNote)}</p>
        {isCustom && <span className={styles.customBadge}>{formatMessage(messages.customBadge)}</span>}

        <div className={styles.previews}>
          <figure>
            <figcaption>{formatMessage(messages.currentLabel)}</figcaption>
            <img src={currentThumbnailUrl} alt="Current thumbnail" />
          </figure>
          {previewUrl && (
            <figure>
              <figcaption>{formatMessage(messages.newLabel)}</figcaption>
              <img src={previewUrl} alt="New thumbnail preview" data-test="new-thumbnail-preview" />
            </figure>
          )}
        </div>

        <FileUpload
          className={styles.dropzone}
          options={{
            accept: ['image/jpeg', 'image/png', 'image/webp'],
            maxFiles: 1,
            maxSize: MAX_UPLOAD_BYTES,
            multiple: false,
            disabled: busy,
          }}
          onDrop={(files) => setPendingFile(files[0] ?? null)}
        />

        {storyboard && (
          <p className={styles.storyboardOption}>
            {formatMessage(messages.orStoryboard)}{' '}
            <button
              type="button"
              className={styles.storyboardLink}
              disabled={busy}
              onClick={() => showModal(<StoryboardViewerModal {...storyboard} canSetThumbnail />)}
              data-test="thumbnail-from-storyboard"
            >
              {formatMessage(messages.chooseFrame, { count: storyboard.count })}
            </button>
          </p>
        )}

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.revertLink}
            disabled={!isCustom || busy}
            onClick={() => revertMutation.mutate()}
            data-test="revert-thumbnail"
          >
            {revertMutation.isLoading ? formatMessage(messages.processing) : formatMessage(messages.revert)}
          </button>
          <Button
            mode="primary"
            size="small"
            disabled={!pendingFile || busy}
            loading={uploadMutation.isLoading}
            onClick={() => pendingFile && uploadMutation.mutate(pendingFile)}
            datatest="upload-thumbnail"
          >
            {uploadMutation.isLoading ? formatMessage(messages.processing) : formatMessage(messages.upload)}
          </Button>
        </div>
      </div>
    </BaseModal>
  );
};

export default ChangeThumbnailDialog;
